import Box from '@mui/material/Box';
import { styled, alpha } from '@mui/material/styles';

interface FilterTab {
  value: string;
  label: string;
  count?: number;
}

interface FilterTabsProps {
  tabs: FilterTab[];
  value: string;
  onChange: (value: string) => void;
}

const Container = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
});

const Tab = styled('button', {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive: boolean }>(({ theme, isActive }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '8px 16px',
  borderRadius: 9999,
  border: isActive ? 'none' : `1px solid ${theme.palette.divider}`,
  backgroundColor: isActive ? theme.palette.success.main : theme.palette.background.paper,
  color: isActive ? '#ffffff' : theme.palette.text.secondary,
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontSize: '0.875rem',
  fontWeight: 500,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: isActive
      ? theme.palette.success.dark
      : alpha(theme.palette.text.primary, 0.04),
  },
  '&:focus': {
    outline: 'none',
    boxShadow: `0 0 0 3px ${alpha(theme.palette.success.main, 0.2)}`,
  },
}));

const Count = styled('span', {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive: boolean }>(({ theme, isActive }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 20,
  height: 20,
  padding: '0 6px',
  borderRadius: 10,
  fontSize: '0.75rem',
  fontWeight: 600,
  backgroundColor: isActive
    ? 'rgba(255, 255, 255, 0.2)'
    : alpha(theme.palette.text.primary, 0.08),
  color: isActive ? '#ffffff' : theme.palette.text.secondary,
}));

export function FilterTabs({ tabs, value, onChange }: FilterTabsProps) {
  return (
    <Container>
      {tabs.map((tab) => {
        const isActive = tab.value === value;
        return (
          <Tab
            key={tab.value}
            isActive={isActive}
            onClick={() => onChange(tab.value)}
            type="button"
          >
            {tab.label}
            {tab.count !== undefined && <Count isActive={isActive}>{tab.count}</Count>}
          </Tab>
        );
      })}
    </Container>
  );
}
